import React from 'react';
import { Card, CardContent, Typography, Box, Grid, LinearProgress } from '@mui/material';
import { useIntl, FormattedMessage } from 'react-intl';
import { designTokens } from '../theme/designTokens';
import { Speed, TrendingUp, TrendingDown } from '@mui/icons-material';

interface PredictiveAnalyticsProps {
    currentMonthExpenses: number;
    totalIncome: number; // Used as Budget
    daysInMonth: number;
    daysElapsed: number;
    showValues?: boolean;
}

const PredictiveAnalytics: React.FC<PredictiveAnalyticsProps> = ({
    currentMonthExpenses,
    totalIncome,
    daysInMonth,
    daysElapsed,
    showValues = true,
}) => {
    const intl = useIntl();

    // Avoid division by zero
    const safeDaysElapsed = Math.max(1, daysElapsed);

    // Calculate Average Daily Spend
    const averageDailySpend = currentMonthExpenses / safeDaysElapsed;

    // Projected Total Spend = Average * Total Days
    const projectedTotalSpend = averageDailySpend * daysInMonth;

    // Projected Balance = Income - Projected Spend
    const projectedBalance = totalIncome - projectedTotalSpend;

    // Spending Velocity Logic
    // Target Daily Spend = Income / Total Days
    // Velocity = Average Daily Spend / Target Daily Spend
    // < 100% = Good (Under Budget)
    // > 100% = Bad (Over Budget)
    const targetDailySpend = totalIncome / daysInMonth;
    const velocityRatio = targetDailySpend > 0 ? (averageDailySpend / targetDailySpend) * 100 : 0;

    const isOverBudget = velocityRatio > 100;
    const velocityColor = isOverBudget ? designTokens.colors.error.main : designTokens.colors.success.main;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Card sx={{
                    height: '100%',
                    borderRadius: designTokens.borderRadius.lg,
                    boxShadow: designTokens.shadows.md,
                    border: designTokens.glassmorphism.border,
                    background: designTokens.colors.background.paper,
                }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TrendingUp sx={{ color: designTokens.colors.primary.main, mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.semibold }}>
                                <FormattedMessage id="projectedBalance" defaultMessage="End-of-Month Projection" />
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            <FormattedMessage id="projectedBalanceDesc" defaultMessage="Estimated balance if you keep spending like this." />
                        </Typography>

                        <Typography variant="h4" sx={{
                            fontWeight: designTokens.typography.fontWeight.bold,
                            color: projectedBalance >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
                            mt: 2
                        }}>
                            {showValues ? `€${Math.round(projectedBalance).toLocaleString()}` : '••••••'}
                        </Typography>

                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: designTokens.colors.text.secondary }}>
                            <FormattedMessage
                                id="avgDailySpend"
                                defaultMessage="Avg. Daily Spend: {amount}"
                                values={{ amount: showValues ? `€${averageDailySpend.toFixed(2)}` : '••••' }}
                            />
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card sx={{
                    height: '100%',
                    borderRadius: designTokens.borderRadius.lg,
                    boxShadow: designTokens.shadows.md,
                    border: designTokens.glassmorphism.border,
                    background: designTokens.colors.background.paper,
                }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Speed sx={{ color: velocityColor, mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.semibold }}>
                                <FormattedMessage id="spendingVelocity" defaultMessage="Spending Velocity" />
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            <FormattedMessage id="spendingVelocityDesc" defaultMessage="How fast you are burning your budget." />
                        </Typography>

                        <Box sx={{ mt: 3, mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: velocityColor }}>
                                    {velocityRatio.toFixed(0)}%
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {isOverBudget
                                        ? <FormattedMessage id="velocityFast" defaultMessage="Too Fast" />
                                        : <FormattedMessage id="velocityGood" defaultMessage="On Track" />
                                    }
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(velocityRatio, 100)}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    bgcolor: designTokens.colors.background.elevated,
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: velocityColor,
                                        borderRadius: 5,
                                    }
                                }}
                            />
                        </Box>

                        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: designTokens.colors.text.secondary }}>
                            <FormattedMessage
                                id="velocityNote"
                                defaultMessage="{status} than usual."
                                values={{
                                    status: isOverBudget
                                        ? intl.formatMessage({ id: 'faster', defaultMessage: 'Faster' })
                                        : intl.formatMessage({ id: 'slower', defaultMessage: 'Slower' })
                                }}
                            />
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default PredictiveAnalytics;
